import { BookOpen, Lightbulb, Code, Palette, Search, SkipForward } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, ERROR, WARNING, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import type { PlanResearchContext, ResearchFinding } from '@/types/plan';
import { RESEARCH_SOURCE_ICONS } from '@/types/plan';
import type { LucideIcon } from 'lucide-react';

const SOURCE_ICON_COMPONENTS: Record<string, LucideIcon> = { BookOpen, Lightbulb, Code, Palette };

function getSourceIcon(sourceType: ResearchFinding['sourceType']): LucideIcon {
  const name = RESEARCH_SOURCE_ICONS[sourceType];
  return SOURCE_ICON_COMPONENTS[name] ?? BookOpen;
}

function extractDomain(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

interface PlanResearchPanelProps {
  researchContext: PlanResearchContext;
  isLoading: boolean;
  onSkipResearch?: () => void;
  currentTopic?: string;
  topicIndex?: number;
  totalTopics?: number;
  urlsScraped?: number;
}

export function PlanResearchPanel({
  researchContext,
  isLoading,
  onSkipResearch,
  currentTopic,
  topicIndex,
  totalTopics,
  urlsScraped,
}: PlanResearchPanelProps) {
  const totalFindings = researchContext.findings.length;

  // Group findings by topic
  const groupedByTopic = researchContext.findings.reduce<Record<string, ResearchFinding[]>>((acc, finding) => {
    (acc[finding.topic] ??= []).push(finding);
    return acc;
  }, {});

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        padding: 16,
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
        <Search className="w-4 h-4" style={{ color: FOREST_GREEN.DEFAULT }} />
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          Research Findings ({totalFindings} source{totalFindings !== 1 ? 's' : ''})
        </span>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div
          aria-live="polite"
          style={{
            fontSize: 12,
            color: FOREST_GREEN.DEFAULT,
            padding: '8px 12px',
            background: `${FOREST_GREEN.DEFAULT}06`,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          Researching topic {topicIndex ?? '?'}/{totalTopics ?? '?'}: {currentTopic ?? '...'}
          {urlsScraped != null && ` (${urlsScraped} URLs scraped)`}
        </div>
      )}

      {/* Grouped findings */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {Object.entries(groupedByTopic).map(([topic, findings]) => (
          <details key={topic} open={Object.keys(groupedByTopic).length <= 3}>
            <summary
              style={{
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                padding: '6px 0',
                listStyle: 'revert',
              }}
            >
              {topic}
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 11,
                  fontWeight: 500,
                  color: MID_GRAY.DEFAULT,
                  background: `${MID_GRAY.DEFAULT}10`,
                  padding: '1px 6px',
                  borderRadius: 10,
                }}
              >
                {findings.length}
              </span>
            </summary>

            <div style={{ paddingLeft: 8, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
              {findings.map((finding) => {
                const SrcIcon = getSourceIcon(finding.sourceType);
                return (
                  <div key={finding.id} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                    <SrcIcon className="w-4 h-4 flex-shrink-0" style={{ color: MID_GRAY.DEFAULT, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>
                        <span style={{ color: FOREST_GREEN.DEFAULT }}>{extractDomain(finding.url)}</span>
                        {' — '}
                        {finding.title}
                      </div>
                      <div style={{ color: MID_GRAY.DEFAULT, marginTop: 2, lineHeight: 1.4 }}>
                        {finding.summary.length > 100 ? `${finding.summary.slice(0, 100)}...` : finding.summary}
                      </div>
                      {/* Relevance bar */}
                      <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                        <span style={{ color: MID_GRAY.lighter, fontSize: 11 }}>Relevance:</span>
                        <div style={{ flex: 1, maxWidth: 100, height: 4, borderRadius: 2, background: `${MID_GRAY.DEFAULT}20` }}>
                          <div
                            style={{
                              width: `${Math.round(finding.relevanceScore * 100)}%`,
                              height: '100%',
                              borderRadius: 2,
                              background: finding.relevanceScore > 0.7
                                ? FOREST_GREEN.DEFAULT
                                : finding.relevanceScore > 0.4
                                  ? WARNING.DEFAULT
                                  : ERROR.DEFAULT,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                        <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', color: MID_GRAY.DEFAULT }}>
                          {finding.relevanceScore.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        ))}
      </div>

      {/* Synthesized insight */}
      {researchContext.synthesizedInsight && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 6,
            background: FOREST_GREEN.faint + '20',
            border: `1px solid ${FOREST_GREEN.DEFAULT}20`,
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <div className="flex items-center gap-1.5" style={{ fontWeight: 600, marginBottom: 4, color: FOREST_GREEN.DEFAULT }}>
            <Lightbulb className="w-3.5 h-3.5" />
            Synthesized Insight
          </div>
          <p style={{ margin: 0, color: FOREST_GREEN.light }}>{researchContext.synthesizedInsight}</p>
        </div>
      )}

      {/* Skip button */}
      {isLoading && onSkipResearch && (
        <button
          type="button"
          onClick={onSkipResearch}
          className="flex items-center gap-1.5"
          style={{
            marginTop: 12,
            padding: '6px 12px',
            borderRadius: 6,
            border: `1px solid ${MID_GRAY.DEFAULT}30`,
            background: 'none',
            cursor: 'pointer',
            fontSize: 12,
            color: MID_GRAY.DEFAULT,
          }}
        >
          <SkipForward className="w-3.5 h-3.5" />
          Skip Research ({totalFindings} finding{totalFindings !== 1 ? 's' : ''} so far)
        </button>
      )}
    </div>
  );
}
